import { useEffect, useId, useRef, useState } from 'react';
import { Button } from '../ui/Button';
import { formatCustodyFailure } from '../../api/apiError';
import {
  EVIDENCE_FACT_CODES,
  PAPER_ORIGINAL_VALUES,
  RELATIONSHIP_TYPES,
  SOURCE_FORM_VALUES,
  admissionDraftResetKey,
  baselineFactDraft,
  diffAdmissionFacts,
} from '../../admission/admissionFacts';
import { confirmAdmissionFacts } from '../../admission/confirmAdmissionFacts';
import { ADMISSION_READINESS } from '../../admission/labels';

const inputClass =
  'mt-1 w-full rounded-xl border border-eorig-border bg-white px-3 py-2 text-sm text-eorig-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-eorig-cyan/35 disabled:bg-eorig-surface';

function Field({ id, label, hint, children }) {
  return (
    <div>
      <label htmlFor={id} className="block text-[12px] font-semibold text-eorig-ink">
        <span className="font-mono">{label}</span>
        {hint ? <span className="ml-2 font-sans font-medium text-eorig-muted">{hint}</span> : null}
      </label>
      {children}
    </div>
  );
}

function ReasonField({ id, label, value, onChange, disabled }) {
  return (
    <input
      id={id}
      aria-label={label}
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
      placeholder="Reason code"
      className={inputClass}
    />
  );
}

export function AdmissionFactsForm({
  packageId,
  documentId,
  admission,
  readOnly = false,
  onAdmissionUpdated,
  onActivityChange,
}) {
  const baseId = useId();
  const mounted = useRef(true);
  const [draft, setDraft] = useState(() => baselineFactDraft(admission, documentId));
  const [reasons, setReasons] = useState({});
  const [forceReasons, setForceReasons] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const baseline = baselineFactDraft(admission, documentId);
  const draftResetKey = admissionDraftResetKey(admission, documentId);
  const documents = admission?.document_assessments || [];
  const locked =
    readOnly ||
    admission?.readiness_status === ADMISSION_READINESS.CERTIFIED ||
    admission?.readiness_status === ADMISSION_READINESS.EVALUATING;
  const diff = diffAdmissionFacts(baseline, draft, reasons, documentId, { forceReasons });
  const canSave =
    !locked &&
    !saving &&
    packageId &&
    Number(admission?.row_version) >= 1 &&
    diff.facts.length > 0 &&
    diff.missingReasons.length === 0 &&
    diff.invalid.length === 0;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    setDraft(baselineFactDraft(admission, documentId));
    setReasons({});
    setForceReasons(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by row_version + document
  }, [draftResetKey]);

  function patchDraft(partial) {
    setDraft((current) => ({ ...current, ...partial }));
    setError('');
  }

  function patchReason(key, value) {
    setReasons((current) => ({ ...current, [key]: value }));
    setError('');
  }

  async function handleSave() {
    const nextDiff = diffAdmissionFacts(baseline, draft, reasons, documentId, { forceReasons });
    if (nextDiff.invalid.length) {
      setError(`Enter a value for ${nextDiff.invalid.join(', ')}.`);
      return;
    }
    if (nextDiff.missingReasons.length) {
      setError(`A reason code is required to correct ${nextDiff.missingReasons.join(', ')}.`);
      return;
    }
    if (!nextDiff.facts.length || !packageId) return;

    setSaving(true);
    setError('');
    setNotice('');
    onActivityChange?.(true);
    try {
      const result = await confirmAdmissionFacts({
        packageId,
        reviewedRowVersion: admission.row_version,
        facts: nextDiff.facts,
      });
      onAdmissionUpdated?.(result.admission);
      if (mounted.current) {
        if (result.admission) {
          setDraft(baselineFactDraft(result.admission, documentId));
          setReasons({});
        }
        setNotice(
          result.reevaluated
            ? 'Facts saved. Admission checks ran again. Certify with the new snapshot.'
            : 'Facts saved.'
        );
      }
    } catch (requestError) {
      if (requestError?.admission) onAdmissionUpdated?.(requestError.admission);
      if (mounted.current) {
        if (requestError?.code === 'FACT_CORRECTION_REASON_REQUIRED') {
          setForceReasons(true);
        }
        setError(formatCustodyFailure(requestError, 'Unable to save admission facts.'));
      }
    } finally {
      onActivityChange?.(false);
      if (mounted.current) setSaving(false);
    }
  }

  function showReason(before, after) {
    const dirty = String(before || '') !== String(after || '');
    if (!dirty) return false;
    return forceReasons || String(before || '').trim() !== '';
  }

  return (
    <section className="space-y-3">
      <div>
        <h3 className="text-[12px] font-semibold uppercase tracking-wide text-eorig-muted">
          Originator facts
        </h3>
        <p className="mt-1 text-[12px] text-eorig-muted">
          Confirm package facts with the current admission row. A change runs admission checks
          again before certification.
        </p>
      </div>

      {!packageId || Number(admission?.row_version) < 1 ? (
        <p className="text-[13px] text-eorig-muted">
          Load admission before saving facts.
        </p>
      ) : null}

      <Field id={`${baseId}-control-party`} label="CONTROL_PARTY" hint="Package">
        <input
          id={`${baseId}-control-party`}
          value={draft.CONTROL_PARTY}
          disabled={locked || saving}
          onChange={(event) => patchDraft({ CONTROL_PARTY: event.target.value })}
          className={inputClass}
        />
        {showReason(baseline.CONTROL_PARTY, draft.CONTROL_PARTY) ? (
          <ReasonField
            id={`${baseId}-control-party-reason`}
            label="CONTROL_PARTY reason code"
            value={reasons.CONTROL_PARTY || ''}
            disabled={locked || saving}
            onChange={(value) => patchReason('CONTROL_PARTY', value)}
          />
        ) : null}
      </Field>

      <Field id={`${baseId}-legal-capacity`} label="LEGAL_CAPACITY" hint="Package">
        <input
          id={`${baseId}-legal-capacity`}
          value={draft.LEGAL_CAPACITY}
          disabled={locked || saving}
          onChange={(event) => patchDraft({ LEGAL_CAPACITY: event.target.value })}
          className={inputClass}
        />
        {showReason(baseline.LEGAL_CAPACITY, draft.LEGAL_CAPACITY) ? (
          <ReasonField
            id={`${baseId}-legal-capacity-reason`}
            label="LEGAL_CAPACITY reason code"
            value={reasons.LEGAL_CAPACITY || ''}
            disabled={locked || saving}
            onChange={(value) => patchReason('LEGAL_CAPACITY', value)}
          />
        ) : null}
      </Field>

      <Field id={`${baseId}-source-form`} label="SOURCE_FORM" hint="Package">
        <select
          id={`${baseId}-source-form`}
          value={draft.SOURCE_FORM}
          disabled={locked || saving}
          onChange={(event) => patchDraft({ SOURCE_FORM: event.target.value })}
          className={inputClass}
        >
          <option value="">Not confirmed</option>
          {SOURCE_FORM_VALUES.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
        {showReason(baseline.SOURCE_FORM, draft.SOURCE_FORM) ? (
          <ReasonField
            id={`${baseId}-source-form-reason`}
            label="SOURCE_FORM reason code"
            value={reasons.SOURCE_FORM || ''}
            disabled={locked || saving}
            onChange={(value) => patchReason('SOURCE_FORM', value)}
          />
        ) : null}
      </Field>

      <Field id={`${baseId}-paper`} label="PAPER_ORIGINAL_STATUS" hint="Package">
        <select
          id={`${baseId}-paper`}
          value={draft.PAPER_ORIGINAL_STATUS}
          disabled={locked || saving}
          onChange={(event) => patchDraft({ PAPER_ORIGINAL_STATUS: event.target.value })}
          className={inputClass}
        >
          <option value="">Not confirmed</option>
          {PAPER_ORIGINAL_VALUES.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
        {showReason(baseline.PAPER_ORIGINAL_STATUS, draft.PAPER_ORIGINAL_STATUS) ? (
          <ReasonField
            id={`${baseId}-paper-reason`}
            label="PAPER_ORIGINAL_STATUS reason code"
            value={reasons.PAPER_ORIGINAL_STATUS || ''}
            disabled={locked || saving}
            onChange={(value) => patchReason('PAPER_ORIGINAL_STATUS', value)}
          />
        ) : null}
      </Field>

      <Field id={`${baseId}-primary`} label="PRIMARY_DOCUMENT" hint="Package">
        <select
          id={`${baseId}-primary`}
          value={draft.PRIMARY_DOCUMENT}
          disabled={locked || saving}
          onChange={(event) => patchDraft({ PRIMARY_DOCUMENT: event.target.value })}
          className={inputClass}
        >
          <option value="">Not confirmed</option>
          {documents.map((row) => (
            <option key={row.document_id} value={row.document_id}>
              {row.document_id}
            </option>
          ))}
        </select>
        {showReason(baseline.PRIMARY_DOCUMENT, draft.PRIMARY_DOCUMENT) ? (
          <ReasonField
            id={`${baseId}-primary-reason`}
            label="PRIMARY_DOCUMENT reason code"
            value={reasons.PRIMARY_DOCUMENT || ''}
            disabled={locked || saving}
            onChange={(value) => patchReason('PRIMARY_DOCUMENT', value)}
          />
        ) : null}
      </Field>

      {EVIDENCE_FACT_CODES.map((code) => (
        <Field key={code} id={`${baseId}-${code}`} label={code} hint="Package">
          <select
            id={`${baseId}-${code}`}
            value={draft[code]}
            disabled={locked || saving}
            onChange={(event) => patchDraft({ [code]: event.target.value })}
            className={inputClass}
          >
            <option value="">Not confirmed</option>
            <option value="true">true</option>
            <option value="false">false</option>
          </select>
          {showReason(baseline[code], draft[code]) || (forceReasons && draft[code]) ? (
            <ReasonField
              id={`${baseId}-${code}-reason`}
              label={`${code} reason code`}
              value={reasons[code] || ''}
              disabled={locked || saving}
              onChange={(value) => patchReason(code, value)}
            />
          ) : null}
        </Field>
      ))}

      {documentId ? (
        <div className="space-y-3 border-t border-eorig-border pt-3">
          <p className="text-[12px] font-semibold text-eorig-ink">This document</p>
          <Field id={`${baseId}-doc-source`} label="SOURCE_FORM" hint="Document">
            <select
              id={`${baseId}-doc-source`}
              aria-label="Document SOURCE_FORM"
              value={draft.documentSourceForm}
              disabled={locked || saving}
              onChange={(event) => patchDraft({ documentSourceForm: event.target.value })}
              className={inputClass}
            >
              <option value="">Not confirmed</option>
              {SOURCE_FORM_VALUES.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
            {showReason(baseline.documentSourceForm, draft.documentSourceForm) ? (
              <ReasonField
                id={`${baseId}-doc-source-reason`}
                label="Document SOURCE_FORM reason code"
                value={reasons.DOCUMENT_SOURCE_FORM || ''}
                disabled={locked || saving}
                onChange={(value) => patchReason('DOCUMENT_SOURCE_FORM', value)}
              />
            ) : null}
          </Field>

          <Field id={`${baseId}-doc-paper`} label="PAPER_ORIGINAL_STATUS" hint="Document">
            <select
              id={`${baseId}-doc-paper`}
              aria-label="Document PAPER_ORIGINAL_STATUS"
              value={draft.documentPaperStatus}
              disabled={locked || saving}
              onChange={(event) => patchDraft({ documentPaperStatus: event.target.value })}
              className={inputClass}
            >
              <option value="">Not confirmed</option>
              {PAPER_ORIGINAL_VALUES.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
            {showReason(baseline.documentPaperStatus, draft.documentPaperStatus) ||
            (forceReasons && draft.documentPaperStatus) ? (
              <ReasonField
                id={`${baseId}-doc-paper-reason`}
                label="Document PAPER_ORIGINAL_STATUS reason code"
                value={reasons.DOCUMENT_PAPER_ORIGINAL_STATUS || ''}
                disabled={locked || saving}
                onChange={(value) => patchReason('DOCUMENT_PAPER_ORIGINAL_STATUS', value)}
              />
            ) : null}
          </Field>

          <Field id={`${baseId}-relationship`} label="DOCUMENT_RELATIONSHIP" hint="Document">
            <select
              id={`${baseId}-relationship`}
              value={draft.relationshipType}
              disabled={locked || saving}
              onChange={(event) =>
                patchDraft({
                  relationshipType: event.target.value,
                  relationshipTarget:
                    event.target.value === 'PRIMARY' ? '' : draft.relationshipTarget,
                })
              }
              className={inputClass}
            >
              <option value="">Not confirmed</option>
              {RELATIONSHIP_TYPES.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
            {draft.relationshipType && draft.relationshipType !== 'PRIMARY' ? (
              <select
                id={`${baseId}-relationship-target`}
                aria-label="DOCUMENT_RELATIONSHIP target"
                value={draft.relationshipTarget}
                disabled={locked || saving}
                onChange={(event) => patchDraft({ relationshipTarget: event.target.value })}
                className={inputClass}
              >
                <option value="">Select document</option>
                {documents
                  .filter((row) => String(row.document_id) !== String(documentId))
                  .map((row) => (
                    <option key={row.document_id} value={row.document_id}>
                      {row.document_id}
                    </option>
                  ))}
              </select>
            ) : null}
            {draft.relationshipType &&
            (forceReasons || baseline.relationshipType) &&
            (baseline.relationshipType !== draft.relationshipType ||
              baseline.relationshipTarget !== draft.relationshipTarget) ? (
              <ReasonField
                id={`${baseId}-relationship-reason`}
                label="DOCUMENT_RELATIONSHIP reason code"
                value={reasons.DOCUMENT_RELATIONSHIP || ''}
                disabled={locked || saving}
                onChange={(value) => patchReason('DOCUMENT_RELATIONSHIP', value)}
              />
            ) : null}
          </Field>
        </div>
      ) : null}

      {locked ? (
        <p className="text-[12px] text-eorig-muted">
          {admission?.readiness_status === ADMISSION_READINESS.EVALUATING
            ? 'Admission checks are running.'
            : 'Certified admission facts cannot be changed here.'}
        </p>
      ) : (
        <Button variant="navy" onClick={handleSave} disabled={!canSave}>
          {saving ? 'Saving…' : 'Save facts'}
        </Button>
      )}

      {notice ? <p className="text-[12px] font-medium text-emerald-700">{notice}</p> : null}
      {error ? <p className="text-[12px] font-medium text-rose-600">{error}</p> : null}
    </section>
  );
}
